// Swap ONLY the lightweight cart-items-list regions (line items + price summary + footer total)
// into the live drawer. Every amount is computed by Shopify/Liquid inside the section — JS never
// does pricing math here, it only moves Shopify-rendered nodes into place. Accepts the HTML string
// of the cart-items-list section (from /cart?section_id=cart-items-list or an add/change response).
// Returns true if it applied the partial swap. Returns false when the live drawer has no
// #CartDrawer-ItemsList yet (cart was empty / drawer shows empty-state) — the caller should then
// do a full drawer render so the chrome (header/footer) appears.
window.applyCartItemsList = function (htmlString) {
  if (!document.getElementById('CartDrawer-ItemsList')) return false;
  const doc = new DOMParser().parseFromString(htmlString, 'text/html');
  ['#CartDrawer-ItemsList', '#cartSummary', '#CartDrawer-TotalWrap'].forEach((selector) => {
    const oldEl = document.querySelector(selector);
    const newEl = doc.querySelector(selector);
    if (oldEl && newEl) oldEl.replaceWith(newEl);
  });
  return true;
};

class CartApplyDiscountButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault(); // Prevent default button behavior
      if(event.target.id != 'apply-discount-btn') {
        return;
      }

      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      
      const discountInput = cartItems.querySelector('#discount-code-input');
      const feedbackMessage = cartItems.querySelector('#discount-feedback');

      if (!discountInput || !feedbackMessage) {
        console.error('Required elements not found in the cart container');
        return;
      }

      const discountCode = discountInput.value.trim();

      if (!discountCode) {
        feedbackMessage.textContent = 'Please enter a discount code.';
        return;
      }

      // Call to apply the discount
      this.applyDiscount(discountCode, feedbackMessage);
    });
  }

  applyDiscount(discountCode, feedbackMessage) {
    fetch(`/discount/${discountCode}`, { method: 'GET' })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to apply discount.');
        // return response.json();
        //console.log('error');
        return;
      })
      .then((data) => {
        //feedbackMessage.textContent = 'Discount applied!';
        // console.log('Discount applied:', data);
        this.updateCartContents();
      })
      .catch((error) => {
        feedbackMessage.textContent = error.message;
        console.error('Error applying discount:', error);
      });
  }

  removeDiscount(discountCode, feedbackMessage) {
    fetch(`/discount/NO_DISCOUNT`, { method: 'GET' })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to apply discount.');
        // return response.json();
        //console.log('error');
        return;
      })
      .then((data) => {
        //feedbackMessage.textContent = 'Discount applied!';
        // console.log('Discount Removed');
        this.updateCartContents();
      })
      .catch((error) => {
        feedbackMessage.textContent = error.message;
        console.error('Error applying discount:', error);
      });
  }

  updateCartContents() {
    // Re-render only the lightweight cart-items-list (items + price summary), not the whole drawer.
    fetch(`${routes.cart_url}?section_id=cart-items-list`)
      .then((response) => response.text())
      .then((responseText) => {
        const newDocument = new DOMParser().parseFromString(responseText, 'text/html');
        // Cart emptied: lightweight section has no empty-state — full drawer render instead.
        if (!newDocument.querySelector('.cart-item')) {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderFullCartDrawer === 'function') drawer.renderFullCartDrawer();
          return;
        }
        requestAnimationFrame(() => {
          const applied = typeof window.applyCartItemsList === 'function' && window.applyCartItemsList(responseText);
          const drawer = document.querySelector('cart-drawer');
          if (!applied && drawer && typeof drawer.renderFullCartDrawer === 'function') drawer.renderFullCartDrawer();
        });
      })
      .catch((error) => {
        console.error('Error updating cart contents:', error);
      });
  }
}
customElements.define('cart-discount-button', CartApplyDiscountButton);

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }
  setHeaderCartIconAccessibility() {
    const setupCartLink = (selector) => {
      const cartLink = document.querySelector(selector);
      if (!cartLink) {
        console.warn(`Element with selector "${selector}" not found.`);
        return;
      }
  
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    };
  
    // Apply the function to both selectors
    setupCartLink('#cart-icon-bubble');
    setupCartLink('#cart-icon--bubble');
    setupCartLink('#cart-notification-button'); // notification popup view cart click
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    typeof(renderPopPrices)==='function' && renderPopPrices(true);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.style.overflow = '';


    const cartCountBubble = document.querySelector('.cart-count-bubble span:first-child');
    const cartCount = cartCountBubble ? cartCountBubble.textContent.trim() : null;
    const cartBubble = document.querySelector('#cart--bubble');
    if (cartBubble) cartBubble.innerText = cartCount;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;

    const itemsHTML = parsedState.sections && parsedState.sections['cart-items-list'];
    const bubbleHTML = parsedState.sections && parsedState.sections['cart-icon-bubble'];

    // An empty cart needs the full drawer markup (empty-state + hidden header/footer), which the
    // lightweight section doesn't carry. Rare on add — fall back to a full drawer render.
    if (parsedState.item_count === 0 || !itemsHTML) {
      this.renderFullCartDrawer();
      return;
    }

    requestAnimationFrame(() => {
      // Drawer was empty (no items-list node to swap) → need the full drawer chrome.
      if (!window.applyCartItemsList(itemsHTML)) {
        this.renderFullCartDrawer();
        return;
      }
      if (bubbleHTML) {
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) bubble.innerHTML = this.getSectionInnerHTML(bubbleHTML, '.shopify-section');
      }
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  // Heavy fallback: re-render the whole drawer interior. Used only when the cart becomes empty,
  // so the empty-state markup and hidden header/footer appear correctly.
  renderFullCartDrawer() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const doc = new DOMParser().parseFromString(responseText, 'text/html');
        const newInner = doc.querySelector('#CartDrawer .drawer__inner') || doc.querySelector('.drawer__inner');
        const liveInner = this.querySelector('.drawer__inner');
        if (!newInner || !liveInner) return;
        requestAnimationFrame(() => {
          liveInner.innerHTML = newInner.innerHTML;
          const overlay = this.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', this.close.bind(this));
          this.open();
        });
      })
      .catch((error) => console.error('Error rendering full cart drawer:', error));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    // Add-to-cart asks Shopify to render only the lightweight cart-items-list (+ icon bubble),
    // NOT the whole heavy cart-drawer section. This is the main server-time saving.
    return [
      {
        id: 'cart-items-list',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}
customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}
customElements.define('cart-drawer-items', CartDrawerItems);
